import { useCallback, useEffect, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { v4 as uuidV4 } from 'uuid';

import SocketService from '@src/services/SocketService';

import { useRoomContext } from '@src/contexts';

import { createScreenshotFileName } from '@src/utils';

import { useMagicPenActions } from '@src/hooks';
import { useChatStore, useDrawerStore, useMagicPenStore, useRoomStore } from '@src/stores';

import { EMessageType, IMessage, IReceiverMessageData, ECommonSocketKeys } from '@src/types';
import { ISaveAndShareModalProps } from './saveAndShare.types';

const useSaveAndShareModal = ({ canvasRef, videoRef, magicPenCanvasRef }: ISaveAndShareModalProps) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const { handleStopAnnotating } = useMagicPenActions();

  const { isSaveModalOpen, setSaveModalState, saveModalTriggerSource } = useMagicPenStore(
    useShallow((state) => ({
      isSaveModalOpen: state.isSaveModalOpen,
      setSaveModalState: state.setSaveModalState,
      saveModalTriggerSource: state.saveModalTriggerSource,
    }))
  );

  const { appendMessage, setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({
      appendMessage: state.appendMessage,
      setSelectedReceiverData: state.setSelectedReceiverData,
    }))
  );

  const { toggleDrawer, setSelectedKey } = useDrawerStore(
    useShallow((state) => ({
      toggleDrawer: state.toggleDrawer,
      setSelectedKey: state.setSelectedKey,
    }))
  );

  const { currentPeer } = useRoomContext();

  // Get room title from room store
  const { roomId, roomTitle } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
      roomTitle: state.roomTitle,
      setIsMagicPenVisible: state.setIsMagicPenVisible,
      isMagicPenVisible: state.isMagicPenVisible,
    }))
  );

  // Capture screenshot of both video and canvas
  const captureScreenshot = useCallback(async () => {
    if (!canvasRef.current) return null;

    setIsCapturing(true);

    try {
      // Create a temporary canvas to combine video and magic pen canvas
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      // Get the MagicPen container dimensions
      const containerRect = canvasRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Set canvas dimensions to match the container
      tempCanvas.width = containerWidth;
      tempCanvas.height = containerHeight;

      // Fill with transparent background initially
      ctx.fillStyle = 'transparent';
      ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw video to fill the entire container (matching CSS object-fit: cover behavior)
      if (videoRef?.current) {
        // Get video dimensions
        const videoWidth = videoRef.current.videoWidth || videoRef.current.clientWidth;
        const videoHeight = videoRef.current.videoHeight || videoRef.current.clientHeight;

        // Calculate aspect ratio and position to match how video is displayed in VideoBox
        // VideoBox uses object-fit: cover, which fills the container while preserving aspect ratio
        const videoAspectRatio = videoWidth / videoHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let renderWidth, renderHeight, offsetX, offsetY;

        if (containerAspectRatio > videoAspectRatio) {
          // Container is wider than video - pillarbox (black bars on sides)
          renderHeight = containerHeight;
          renderWidth = containerHeight * videoAspectRatio;
          offsetX = (containerWidth - renderWidth) / 2;
          offsetY = 0;
        } else {
          // Container is taller than video - letterbox (black bars on top/bottom)
          renderWidth = containerWidth;
          renderHeight = containerWidth / videoAspectRatio;
          offsetX = 0;
          offsetY = (containerHeight - renderHeight) / 2;
        }

        // Draw video frame with proper aspect ratio and positioning to match object-fit: cover
        ctx.drawImage(
          videoRef.current,
          0, // source x
          0, // source y
          videoWidth, // source width
          videoHeight, // source height
          offsetX, // destination x
          offsetY, // destination y
          renderWidth, // destination width
          renderHeight // destination height
        );
      } else {
        // If no video, fill with a default background to match VideoBox
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      }

      // Draw magic pen canvas on top

      if (magicPenCanvasRef?.current?.stageRef?.current) {
        const stage = magicPenCanvasRef.current.stageRef.current;

        // Use Konva's native toDataURL method to get a properly scaled image of the stage
        const stageDataUrl = await new Promise<string>((resolve) => {
          stage.toDataURL({
            callback: resolve,
            mimeType: 'image/png',
          });
        });

        // Create an image from the data URL
        const img = new Image();
        const imgLoadPromise = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = stageDataUrl;
        });

        // Wait for the image to load
        await imgLoadPromise;

        // Draw the MagicPen canvas image to fill the entire container
        ctx.drawImage(
          img,
          0,
          0,
          img.width,
          img.height, // source
          0,
          0,
          containerWidth,
          containerHeight // destination - fill entire container
        );
      }

      // Convert to data URL
      const dataUrl = tempCanvas.toDataURL('image/png');
      return dataUrl;
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, [canvasRef, videoRef, magicPenCanvasRef]);

  // Generate preview when modal opens
  useEffect(() => {
    if (isSaveModalOpen) {
      const generatePreview = async () => {
        const image = await captureScreenshot();
        setPreviewImage(image);
      };
      generatePreview();
    }
  }, [isSaveModalOpen, captureScreenshot]);

  // Save file locally
  const saveFileLocally = useCallback(
    async (dataUrl: string) => {
      try {
        // Create download link
        const link = document.createElement('a');
        link.href = dataUrl;

        // Create filename with room title and current date/time
        const baseName = roomTitle || currentPeer?.peer_info?.peer_name || 'screenshot';
        const fileName = createScreenshotFileName(baseName);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error saving screenshot locally:', error);
      }
    },
    [roomTitle, currentPeer]
  );

  const handleSaveAndShare = useCallback(async () => {
    if (!previewImage) return;

    try {
      // Save file locally first
      await saveFileLocally(previewImage);

      // Create a blob from the data URL
      const res = await fetch(previewImage);
      const blob = await res.blob();

      // Create filename with room title and current date/time
      const baseName = roomTitle || currentPeer?.peer_info?.peer_name || 'screenshot';
      const fileName = createScreenshotFileName(baseName);
      const file = new File([blob], fileName, { type: 'image/png' });

      // Create message object
      const data: IMessage = {
        room_id: roomId,
        peer_id: currentPeer?.id || '',
        peer_name: currentPeer?.peer_info?.peer_name || '',
        to_peer_id: '',
        to_peer_name: '',
        peer_msg: '',
        timestamp: new Date().toISOString(),
        originalTimestamp: new Date().toISOString(),
        chatMessageId: uuidV4(),
        isShown: false,
        peer_record_id: currentPeer?.record_id || '',
        to_peer_record_id: '',
        file: {
          fileName: file.name,
          fileType: file.type,
        },
        type: EMessageType.PUBLIC, // Send to public chat
      };

      // Upload file to server and then emit message
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          data.file = { ...data.file, fileBuffer: reader.result as string };
          const resData = await SocketService.request(ECommonSocketKeys.FILE, data);
          // Merge returned file info into our message payload
          data.file = resData.file;
          SocketService.emit(ECommonSocketKeys.MESSAGE, data);

          // Append to chat
          appendMessage(data);

          // Set receiver to public chat
          setSelectedReceiverData({ key: EMessageType.PUBLIC, value: '' } as IReceiverMessageData);

          // Open chat drawer
          setSelectedKey('chat');
          toggleDrawer(true);

          // Close modal
          setSaveModalState({ isSaveModalOpen: false });
        } catch (error) {
          console.error('Error sharing screenshot:', error);
        }
      };
    } catch (error) {
      console.error('Error processing screenshot:', error);
    }
  }, [
    previewImage,
    roomTitle,
    currentPeer?.peer_info?.peer_name,
    currentPeer?.id,
    currentPeer?.record_id,
    roomId,
    appendMessage,
    setSelectedReceiverData,
    setSelectedKey,
    toggleDrawer,
    setSaveModalState,
    saveFileLocally,
  ]);

  const handleSaveLocal = useCallback(async () => {
    if (!previewImage) return;

    try {
      // Save file locally
      await saveFileLocally(previewImage);

      // Close modal
      setSaveModalState({ isSaveModalOpen: false });
    } catch (error) {
      console.error('Error saving screenshot:', error);
    }
  }, [previewImage, setSaveModalState, saveFileLocally]);

  return {
    isCapturing,
    previewImage,
    isSaveModalOpen,
    previewContainerRef,

    handleSaveLocal,
    handleSaveAndShare,
    setSaveModalState,

    saveModalTriggerSource,
    handleStopAnnotating,
  };
};

export default useSaveAndShareModal;
